import { IconArrowLeft, IconArrowRight, IconContract, IconFileInvoice, IconFiles, IconShoppingCart, IconWorld } from "@tabler/icons-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Button, Card, Group, PageHeader, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { useCrearSolicitud, type SubtipoSolicitud } from "@/features/nueva-solicitud/hooks/use-crear-solicitud";
import { TipoSolicitudValores, type TipoSolicitud } from "@/lib/types";
import { SubtipoCard } from "./SubtipoCard";

const TIPO_CODIGO: Record<number, string> = {
  [TipoSolicitudValores.FB60]: "FB60",
  [TipoSolicitudValores.ContratoAnualizado]: "ContratoAnualizado",
  [TipoSolicitudValores.OrdenDeCompra]: "OrdenDeCompra",
};

/** Preselección de tipo vía search param (quick actions del dashboard). */
export type TipoInicial = "fb60" | "ca" | "oc";
const TIPO_INICIAL_A_VALOR: Record<TipoInicial, TipoSolicitud> = {
  fb60: TipoSolicitudValores.FB60,
  ca: TipoSolicitudValores.ContratoAnualizado,
  oc: TipoSolicitudValores.OrdenDeCompra,
};

/**
 * Pantalla de creación de solicitud · el usuario elige tipo (FB60, Contrato
 * Anualizado u Orden de Compra) y subtipo · al click en "Crear" se ejecuta
 * la mutation que encadena los endpoints y navegamos al paso siguiente
 * según el tipo.
 *
 * `tipoInicial` (del search param `?tipo=`) preselecciona el tipo al entrar
 * desde una acción rápida del dashboard · sin él, arranca en FB60. El usuario
 * sigue pudiendo cambiarlo en la pantalla.
 */
export function NuevaSolicitudPage({ tipoInicial }: { tipoInicial?: TipoInicial }) {
  const navigate = useNavigate();
  const router = useRouter();
  const [tipo, setTipo] = useState<TipoSolicitud>(
    tipoInicial ? TIPO_INICIAL_A_VALOR[tipoInicial] : TipoSolicitudValores.FB60
  );
  const [subtipo, setSubtipo] = useState<SubtipoSolicitud>("CFDI_UNICO");

  const esCA = tipo === TipoSolicitudValores.ContratoAnualizado;
  const esOC = tipo === TipoSolicitudValores.OrdenDeCompra;
  const tipoConDocumento = esCA || esOC;

  const seleccionarTipo = (nuevo: TipoSolicitud) => {
    setTipo(nuevo);
    // OC solo soporta CFDI único. CA soporta CFDI único y Múltiples CFDI (no Contrapago),
    // así que al cambiar a CA solo reseteamos si venía en un subtipo no aplicable.
    // OC soporta CFDI único e Invoice · CA soporta CFDI único, Múltiples e Invoice ·
    // FB60 soporta todos. Al cambiar de tipo, reseteamos solo si el subtipo no aplica.
    if (nuevo === TipoSolicitudValores.OrdenDeCompra && subtipo !== "CFDI_UNICO" && subtipo !== "INVOICE")
      setSubtipo("CFDI_UNICO");
  };

  const crear = useCrearSolicitud({
    onSuccess: (sol, args) =>
      navigate({
        to:
          args.tipo === TipoSolicitudValores.ContratoAnualizado
            ? "/solicitudes/$id/contrato-anualizado"
            : args.tipo === TipoSolicitudValores.OrdenDeCompra
              ? "/solicitudes/$id/orden-compra"
              : args.subtipo === "CFDI_UNICO"
                ? "/solicitudes/$id/cargar-cfdi"
                : args.subtipo === "MULTIPLES_CFDI"
                  ? "/solicitudes/$id/cargar-zip"
                  : args.subtipo === "INVOICE"
                    ? "/solicitudes/$id/carga-invoice"
                    : "/solicitudes/$id/organizacion-manual",
        params: { id: sol.id },
        replace: true,
      }),
  });

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Nueva solicitud de pago"
        description="Elige el tipo y subtipo, y avanza al flujo de captura."
        breadcrumbs={[{ label: "Inicio", href: "/dashboard" }, { label: "Solicitudes", href: "/solicitudes" }, { label: "Nueva" }]}
      />
      <LayoutConStepper estado={null} subtipo={subtipo} tipo={TIPO_CODIGO[tipo]} paso="crear" fillHeight>
        <Stack gap="md">
          <Card padding="lg">
            <Stack gap="lg">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="mb-2 text-[10px] tracking-wide">
                  Tipo
                </Text>
                <Stack gap="sm">
                  <SubtipoCard
                    selected={tipo === TipoSolicitudValores.FB60}
                    onClick={() => seleccionarTipo(TipoSolicitudValores.FB60)}
                    icon={<IconFileInvoice size={20} stroke={1.6} />}
                    title="FB60"
                    description="Factura con CFDI del SAT · pago directo a proveedor."
                    meta="CFDI único o múltiples facturas"
                  />
                  <SubtipoCard
                    selected={esCA}
                    onClick={() => seleccionarTipo(TipoSolicitudValores.ContratoAnualizado)}
                    icon={<IconContract size={20} stroke={1.6} />}
                    title="Contrato Anualizado"
                    description="Pago recurrente contra un contrato anualizado existente en SAP."
                    meta="CFDI único o múltiples facturas"
                  />
                  <SubtipoCard
                    selected={esOC}
                    onClick={() => seleccionarTipo(TipoSolicitudValores.OrdenDeCompra)}
                    icon={<IconShoppingCart size={20} stroke={1.6} />}
                    title="Orden de Compra"
                    description="Pago contra una orden de compra existente en SAP · se concilia con las entradas de almacén recibidas."
                    meta="CFDI único"
                  />
                </Stack>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="mb-2 text-[10px] tracking-wide">
                  Subtipo
                </Text>
                <Stack gap="sm">
                  <SubtipoCard
                    selected={subtipo === "CFDI_UNICO"}
                    onClick={() => setSubtipo("CFDI_UNICO")}
                    icon={<IconFileInvoice size={20} stroke={1.6} />}
                    title="CFDI único"
                    description={
                      esCA
                        ? "Un CFDI que factura contra el contrato anualizado seleccionado."
                        : esOC
                          ? "Un CFDI que factura contra la orden de compra seleccionada."
                          : "Una solicitud con un solo CFDI · sube el XML directamente."
                    }
                    meta="1 factura · 1 póliza SAP"
                  />
                  {/* Múltiples CFDI: FB60 (varios emisores) y Contrato Anualizado (todos del
                      proveedor del contrato). OC no lo soporta. */}
                  {!esOC && (
                    <SubtipoCard
                      selected={subtipo === "MULTIPLES_CFDI"}
                      onClick={() => setSubtipo("MULTIPLES_CFDI")}
                      icon={<IconFiles size={20} stroke={1.6} />}
                      title="Múltiples CFDI"
                      description={
                        esCA
                          ? "ZIP con N pares XML+PDF · todos del proveedor del contrato y del mismo RFC receptor · la suma no puede exceder el saldo del contrato."
                          : "ZIP con N pares XML+PDF · todos del mismo RFC receptor (F6)."
                      }
                      meta="N facturas · N pólizas SAP"
                    />
                  )}
                  {/* Contrapago BLOQUEADO para solicitudes nuevas (decisión del usuario) · el backend
                      también lo rechaza (SOL_SUBTIPO_BLOQUEADO). Las Contrapago existentes siguen su flujo. */}
                  {/* Invoice · factura extranjera en PDF (sin CFDI del SAT) · se extrae por OCR.
                      Aplica a los 3 tipos (FB60 directo · CA/OC contra su documento de SAP). */}
                  <SubtipoCard
                    selected={subtipo === "INVOICE"}
                    onClick={() => setSubtipo("INVOICE")}
                    icon={<IconWorld size={20} stroke={1.6} />}
                    title="Invoice (factura extranjera)"
                    description={
                      esCA
                        ? "Factura extranjera en PDF contra el contrato · se extrae por OCR según la plantilla del cliente."
                        : esOC
                          ? "Factura extranjera en PDF contra la orden de compra · se extrae por OCR según la plantilla del cliente."
                          : "Factura extranjera en PDF · se extrae por OCR según la plantilla del cliente (Bloomberg, NYSE, etc.)."
                    }
                    meta="PDF · sin CFDI del SAT"
                  />
                </Stack>
              </div>
            </Stack>
          </Card>
          <Group justify="space-between">
            <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => router.history.back()}>
              Cancelar
            </Button>
            <Button rightSection={<IconArrowRight size={14} />} loading={crear.isPending} onClick={() => crear.mutate({ tipo, subtipo })}>
              {crear.isPending ? "Creando…" : "Crear y continuar"}
            </Button>
          </Group>
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
